import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Star, ArrowRight, Loader2, Quote } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";
import SEOHead from "@/components/SEOHead";
import { aggregateRatingSchema, reviewSchema } from "@/utils/schemas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultCMS = {
  hero: {
    headline1: "What Our",
    headline2: "Customers Say",
    description: "Don't just take our word for it. See what our satisfied customers have to say about their experience with Overhaul Ops."
  }
};

// Skeleton loader component for Testimonials page
const TestimonialsSkeleton = () => (
  <div data-testid="testimonials-page" className="pt-20">
    <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl animate-pulse">
          <div className="h-4 w-32 bg-white/20 rounded mb-4"></div>
          <div className="h-16 w-80 bg-white/20 rounded mb-6"></div>
          <div className="h-6 w-96 bg-white/20 rounded"></div>
        </div>
      </div>
    </section>
    <section className="bg-white py-12 border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i}>
              <div className="h-12 w-16 bg-gray-200 rounded mx-auto mb-2"></div>
              <div className="h-4 w-20 bg-gray-200 rounded mx-auto"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
    <section className="py-20 md:py-32 bg-[#f4f4f5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white p-8 rounded-sm shadow-sm">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((j) => (
                  <div key={j} className="w-5 h-5 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="space-y-2 mb-6">
                <div className="h-4 w-full bg-gray-200 rounded"></div>
                <div className="h-4 w-full bg-gray-200 rounded"></div>
                <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
              </div>
              <div className="border-t pt-4">
                <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 w-20 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleStats, setGoogleStats] = useState({ total_count: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const { google_review_url } = useSiteConfig();
  const [cms, setCms] = useState(defaultCMS);
  const [activeTab, setActiveTab] = useState("google");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, cmsRes, googleRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/cms/page/testimonials`).catch(() => ({ data: [] })),
          axios.get(`${API}/google-reviews`).catch(() => ({ data: { reviews: [], total_count: 0, average_rating: 0 } }))
        ]);
        setTestimonials(testimonialsRes.data);
        
        if (googleRes.data?.reviews?.length > 0) {
          setGoogleReviews(googleRes.data.reviews);
          setGoogleStats({
            total_count: googleRes.data.total_count || googleRes.data.reviews.length,
            average_rating: googleRes.data.average_rating || 5.0
          });
        }
        
        if (cmsRes.data?.length > 0) {
          const content = { ...defaultCMS };
          cmsRes.data.forEach(item => {
            if (item.section && item.content) {
              content[item.section] = { ...content[item.section], ...item.content };
            }
          });
          setCms(content);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
        setCmsLoaded(true);
      }
    };
    fetchData();
  }, []);

  const testimonialSchemas = useMemo(() => {
    const s = [];
    const reviewCount = googleStats.total_count || testimonials.length;
    const avgRating = googleStats.average_rating || "5.0";
    if (reviewCount > 0) {
      s.push(aggregateRatingSchema(reviewCount, String(avgRating)));
      // Use Google reviews for schema if available, else DB testimonials
      const schemaSource = googleReviews.length > 0 
        ? googleReviews.slice(0, 10).map(r => ({
            name: r.reviewer_name, 
            text: r.comment, 
            rating: r.star_rating === "FIVE" ? 5 : r.star_rating === "FOUR" ? 4 : r.star_rating === "THREE" ? 3 : r.star_rating === "TWO" ? 2 : 1,
            date: r.create_time
          }))
        : testimonials.slice(0, 10).map(t => ({
            name: t.name, text: t.text, rating: t.rating || 5, date: t.date
          }));
      schemaSource.forEach(t => {
        s.push(reviewSchema(t));
      });
    }
    return s;
  }, [testimonials, googleReviews, googleStats]);

  // Show skeleton while CMS loads
  if (!cmsLoaded) {
    return <TestimonialsSkeleton />;
  }

  return (
    <div data-testid="testimonials-page" className="pt-20">
      <SEOHead
        title="Customer Reviews & Testimonials"
        description="See what customers say about Overhaul Ops LLC. 5-star rated junk removal, cleanouts, and hauling in the DFW metroplex."
        keywords="overhaul ops reviews, junk removal reviews DFW, customer testimonials, 5 star junk removal"
        schemas={testimonialSchemas}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Testimonials", url: "/testimonials" }]}
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              Customer Reviews
            </span>
            <h1 
              className="text-5xl md:text-7xl font-black text-white uppercase tracking-wide leading-none mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {cms.hero.headline1}<br />
              <span className="text-[#b51a00]">{cms.hero.headline2}</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {cms.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-12 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <span 
                className="text-4xl md:text-5xl font-black text-[#b51a00]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {googleStats.average_rating > 0 ? googleStats.average_rating.toFixed(1) : "5.0"}
              </span>
              <p className="text-[#707070] text-sm uppercase tracking-wider mt-1">Star Rating</p>
            </div>
            <div>
              <span 
                className="text-4xl md:text-5xl font-black text-[#b51a00]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {googleStats.total_count > 0 ? googleStats.total_count : testimonials.length}+
              </span>
              <p className="text-[#707070] text-sm uppercase tracking-wider mt-1">Reviews</p>
            </div>
            <div>
              <span 
                className="text-4xl md:text-5xl font-black text-[#b51a00]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                DFW
              </span>
              <p className="text-[#707070] text-sm uppercase tracking-wider mt-1">Service Area</p>
            </div>
            <div>
              <span 
                className="text-4xl md:text-5xl font-black text-[#b51a00]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                7
              </span>
              <p className="text-[#707070] text-sm uppercase tracking-wider mt-1">Days a Week</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section data-testid="testimonials-grid" className="py-12 md:py-20 bg-[#f4f4f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab switcher — only show if we have Google reviews */}
          {googleReviews.length > 0 && (
            <div className="flex gap-4 mb-8 justify-center">
              <button
                data-testid="tab-google-reviews"
                onClick={() => setActiveTab("google")}
                className={`px-6 py-2 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors ${
                  activeTab === "google"
                    ? "bg-[#b51a00] text-white"
                    : "bg-white text-[#4a4a4a] hover:bg-gray-100 border"
                }`}
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Google Reviews ({googleReviews.length})
              </button>
              <button
                data-testid="tab-site-testimonials"
                onClick={() => setActiveTab("site")}
                className={`px-6 py-2 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors ${
                  activeTab === "site"
                    ? "bg-[#b51a00] text-white"
                    : "bg-white text-[#4a4a4a] hover:bg-gray-100 border"
                }`}
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Testimonials ({testimonials.length})
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#b51a00]" />
            </div>
          ) : activeTab === "google" && googleReviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {googleReviews.map((review, index) => {
                const ratingMap = { FIVE: 5, FOUR: 4, THREE: 3, TWO: 2, ONE: 1 };
                const stars = ratingMap[review.star_rating] || 5;
                return (
                  <div 
                    key={review.id || index}
                    data-testid={`google-review-${index}`}
                    className="bg-white p-8 rounded-sm shadow-sm hover:shadow-md transition-shadow relative"
                  >
                    <Quote className="absolute top-4 right-4 w-10 h-10 text-[#f4f4f5]" />
                    <div className="star-rating flex gap-1 mb-4">
                      {[...Array(stars)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-current text-[#FFD700]" />
                      ))}
                    </div>
                    {review.comment ? (
                      <p className="text-[#4a4a4a] leading-relaxed mb-6 italic relative z-10">
                        "{review.comment}"
                      </p>
                    ) : (
                      <p className="text-[#707070] leading-relaxed mb-6 italic relative z-10 text-sm">
                        (No written review)
                      </p>
                    )}
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-3">
                        {review.reviewer_photo && (
                          <img src={review.reviewer_photo} alt="" className="w-8 h-8 rounded-full" />
                        )}
                        <div>
                          <p className="font-bold text-[#0f0f0f]">{review.reviewer_name}</p>
                          {review.create_time && (
                            <p className="text-xs text-[#707070]">
                              {new Date(review.create_time).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                            </p>
                          )}
                        </div>
                      </div>
                      {review.reply && (
                        <div className="mt-3 pl-4 border-l-2 border-[#b51a00]/30">
                          <p className="text-xs font-bold text-[#b51a00] mb-1">Owner Response</p>
                          <p className="text-sm text-[#4a4a4a]">{review.reply}</p>
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-4 right-4">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 opacity-30" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.id || index}
                  data-testid={`testimonial-item-${index}`}
                  className="bg-white p-8 rounded-sm shadow-sm hover:shadow-md transition-shadow relative"
                >
                  <Quote className="absolute top-4 right-4 w-10 h-10 text-[#f4f4f5]" />
                  <div className="star-rating flex gap-1 mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current text-[#FFD700]" />
                    ))}
                  </div>
                  <p className="text-[#4a4a4a] leading-relaxed mb-6 italic relative z-10">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-[#0f0f0f]">{testimonial.name}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-[#707070]">{testimonial.location}</p>
                      {testimonial.service && (
                        <span className="text-xs bg-[#b51a00]/10 text-[#b51a00] px-2 py-1 rounded font-medium">
                          {testimonial.service}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Google Review CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#0f0f0f] mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Leave Us a Review
          </h2>
          <p className="text-[#4a4a4a] mb-6 max-w-xl mx-auto">
            Had a great experience with Overhaul Ops? We'd love to hear from you! Leave us a review on Google.
          </p>
          <a 
            href={google_review_url || "https://g.page/r/CeT6wm1so9n5EAI/review"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leave-review-btn"
          >
            <Button 
              className="bg-[#b51a00] hover:bg-[#8f1500] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-4"
            >
              <Star className="w-5 h-5 mr-2 fill-current" />
              Leave a Google Review
            </Button>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#b51a00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Ready to Experience the Difference?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join our satisfied customers. Get a free quote today!
          </p>
          <Link to="/contact">
            <Button 
              data-testid="testimonials-cta-btn"
              className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
            >
              Get Your Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
